/**
 * Layout компонент
 * Красивый современный дизайн с адаптивной навигацией
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import MagneticButton from './MagneticButton';
import { API_URL } from '../utils/api.js';
import { supabase } from '../lib/supabase.js';

const navItems = [
  { path: '/dashboard', label: 'Главная', icon: '🏠', mobileLabel: 'Главная' },
  { path: '/youtube', label: 'YouTube', icon: '📹', mobileLabel: 'YouTube' },
  { path: '/export', label: 'Экспорт', icon: '📤', mobileLabel: 'Экспорт' },
  { path: '/settings', label: 'Настройки', icon: '⚙️', mobileLabel: 'Настройки' },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    checkAuth();
    
    // Отслеживание скролла для улучшенного header
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    
    // Подписываемся на изменения сессии Supabase
    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user) {
          setIsAuthenticated(true);
          setUsername(session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Пользователь');
        } else {
          setIsAuthenticated(false);
          setUsername('');
        }
      });

      return () => {
        window.removeEventListener('scroll', handleScroll);
        authListener?.subscription?.unsubscribe();
      };
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const checkAuth = async () => {
    try {
      // Сначала проверяем Supabase сессию
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setIsAuthenticated(true);
          setUsername(session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Пользователь');
          return;
        }
      }

      // Fallback на старую систему session_token
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        axios.defaults.headers.common['x-session-token'] = sessionToken;
        const response = await axios.get(`${API_URL}/user/session`, {
          params: { token: sessionToken }
        });
        if (response.data.authenticated) {
          setIsAuthenticated(true);
          setUsername(response.data.user?.name || response.data.user?.email || 'Пользователь');
        }
      }
    } catch (error) {
      // Игнорируем ошибки
    }
  };

  const handleLogout = async () => {
    try {
      // Выход из Supabase
      if (supabase) {
        await supabase.auth.signOut();
      }
      
      // Выход из старой системы
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        await axios.post(`${API_URL}/user/logout`, { token: sessionToken });
      }
    } catch (error) {
      console.error('Ошибка выхода:', error);
    } finally {
      // Очищаем все данные пользователя, включая черновики настроек
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('youtube_settings_draft');
      console.log('[Layout] Очищены данные пользователя, включая черновики YouTube настроек');
      delete axios.defaults.headers.common['x-session-token'];
      setIsAuthenticated(false);
      setUsername('');
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Desktop Header - с отступом для safe area на мобильных */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-black/95 backdrop-blur-2xl border-b border-white/5 shadow-lg' 
          : 'bg-black/80 backdrop-blur-xl border-b border-white/5'
      }`} style={{ paddingTop: 'max(env(safe-area-inset-top), 8px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3 group">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center"
              >
                <span className="text-xl">🚀</span>
              </motion.div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white tracking-tighter">AI Market Hub</h1>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} className="relative">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        relative px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2
                        ${isActive 
                          ? 'bg-white/10 text-white border border-white/10' 
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                    >
                      <span className="relative z-10">{item.icon}</span>
                      <span className="relative z-10 tracking-tight">{item.label}</span>
                    </motion.div>
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {isAuthenticated && (
                <>
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-sm text-slate-300 tracking-tight">{username}</span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogout}
                    className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white rounded-lg transition-all tracking-tight"
                  >
                    <span>🚪</span>
                    <span className="hidden lg:inline">Выход</span>
                  </motion.button>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg transition-all tracking-tight
                        ${isActive 
                          ? 'bg-white/10 text-white border border-white/10' 
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
                {isAuthenticated && (
                  <>
                    <div className="border-t border-white/5 my-2" />
                    <div className="flex items-center gap-2 px-4 py-2 text-slate-400">
                      <div className="w-2 h-2 bg-green-400 rounded-full" />
                      <span className="tracking-tight">{username}</span>
                    </div>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 border border-white/10 transition-all tracking-tight"
                    >
                      <span>🚪</span>
                      <span>Выйти</span>
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content - с учётом safe area */}
      <main className="pt-24 pb-24 md:pt-20 md:pb-8 min-h-screen bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/5 z-50 safe-area-inset-bottom">
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex flex-col items-center justify-center py-2 rounded-lg transition-all
                  ${isActive 
                    ? 'text-white bg-white/10 border border-white/10' 
                    : 'text-slate-400 hover:text-slate-300'
                  }
                `}
              >
                <span className="text-xl mb-0.5">{item.icon}</span>
                <span className="text-[10px] font-medium tracking-tight">{item.mobileLabel}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
